import React from 'react';
import Tree, { type CustomNodeElementProps, type TreeNodeDatum } from 'react-d3-tree'; // eslint-disable-line import/no-named-as-default

import cn from '~/Utils/cn';

import { buildTreeData } from './Utils';

const NODE_WIDTH = 220;
const NODE_HEIGHT = 100;
const NODE_SPACING_X = NODE_WIDTH + 40;
const NODE_SPACING_Y = NODE_HEIGHT + 40;
const PADDING = 40;
const CONTAINER_HEIGHT = 500;
const TREE_WIDTH_PADDING_FACTOR = 1.2;

interface TreeNode {
  children?: TreeNode[];
  [key: string]: unknown;
}

interface Unit {
  id: string;
  name: string;
  parent_unit_id?: string;
  leader_user_name?: string;
  members?: { member_user_id: string }[];
  is_active?: boolean;
  type?: string;
}

interface OrgChartNodeDatum extends TreeNodeDatum {
  __isVirtualRoot?: boolean;
  unitData?: Unit;
}

interface OrganizationUnitsChartProps {
  units?: Unit[];
  onActionClick?: (params: { action: string; unit: Unit }) => void;
}

const getTreeDimensions = (node: TreeNode, depth = 0): { depth: number; leafCount: number } => {
  if (!node.children || node.children.length === 0) {
    return { depth, leafCount: 1 };
  }
  let totalLeaves = 0;
  let maxDepth = depth;
  node.children.forEach((child) => {
    const { depth: childDepth, leafCount } = getTreeDimensions(child, depth + 1);
    totalLeaves += leafCount;
    maxDepth = Math.max(maxDepth, childDepth);
  });
  return { depth: maxDepth, leafCount: totalLeaves };
};

const renderCustomNode = ({ nodeDatum }: CustomNodeElementProps, onEditClick: (unit: Unit) => void) => {
  const nodeData = nodeDatum as OrgChartNodeDatum;
  const isVirtualRoot = nodeData.__isVirtualRoot;
  const unitData = nodeData.unitData;
  const isInactive = unitData && !unitData.is_active;
  const InnerElement = isVirtualRoot ? 'div' : 'button';

  const handleClick = () => {
    if (unitData) {
      onEditClick(unitData);
    }
  };

  return (
    <g>
      <foreignObject width={NODE_WIDTH} height={NODE_HEIGHT} x={-NODE_WIDTH / 2} y={-NODE_HEIGHT / 2}>
        <InnerElement
          onClick={handleClick}
          className={cn(
            'flex flex-col items-start justify-center rounded-lg border-2 px-12 py-8 shadow-sm focus-visible:outline focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-teal-700',
            isVirtualRoot ? 'bg-slate-100' : 'bg-white',
            isInactive ? 'border-dashed border-slate-400 opacity-60' : 'border-solid border-blue-700',
            isVirtualRoot ? 'cursor-default' : 'cursor-pointer'
          )}
          style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
        >
          <div className="mb-4 w-full truncate text-base font-semibold">{nodeDatum.name}</div>
          {!isVirtualRoot && (
            <>
              <div className="text-sm text-slate-700">Leader: {nodeDatum.attributes?.Leader}</div>
              <div className="text-sm text-slate-700">Members: {nodeDatum.attributes?.Members}</div>
            </>
          )}
        </InnerElement>
      </foreignObject>
    </g>
  );
};

export const OrganizationUnitsChart = ({ units = [], onActionClick = () => {} }: OrganizationUnitsChartProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [translate, setTranslate] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);

  const treeData = React.useMemo(() => {
    const roots = buildTreeData({ units });

    if (roots.length === 0) return null;
    if (roots.length === 1) return roots[0];

    return {
      name: 'Organization',
      __isVirtualRoot: true,
      attributes: {},
      children: roots,
    };
  }, [units]);

  React.useEffect(() => {
    if (!containerRef.current || !treeData) return;

    const { width: containerWidth } = containerRef.current.getBoundingClientRect();
    const { depth, leafCount } = getTreeDimensions(treeData);

    const treeWidth = leafCount * NODE_SPACING_X * TREE_WIDTH_PADDING_FACTOR;
    const treeHeight = (depth + 1) * NODE_SPACING_Y;

    const scaleX = (containerWidth - PADDING * 2) / Math.max(treeWidth, 1);
    const scaleY = (CONTAINER_HEIGHT - PADDING * 2 - NODE_HEIGHT) / Math.max(treeHeight, 1);
    const fitZoom = Math.min(scaleX, scaleY, 1);

    setZoom(fitZoom);
    setTranslate({
      x: containerWidth / 2,
      y: PADDING + NODE_HEIGHT / 2,
    });
  }, [treeData]);

  const handleEditClick = React.useCallback(
    (unit: Unit) => {
      onActionClick({ action: 'edit', unit });
    },
    [onActionClick]
  );

  if (!treeData) return null;

  return (
    <div ref={containerRef} className="w-full rounded-lg border border-slate-300" style={{ height: CONTAINER_HEIGHT }}>
      <Tree
        data={treeData}
        orientation="vertical"
        translate={translate}
        zoom={zoom}
        nodeSize={{ x: NODE_SPACING_X, y: NODE_SPACING_Y }}
        renderCustomNodeElement={(rd3tProps) => renderCustomNode(rd3tProps, handleEditClick)}
        pathFunc="step"
        separation={{ siblings: 1.2, nonSiblings: 1.5 }}
        collapsible
        zoomable
        draggable
        scaleExtent={{ min: 0.1, max: 2 }}
      />
    </div>
  );
};
